import classNames from 'classnames';
import {
  Map as MapIcon,
  MapBoundary as SurveyAreaIcon,
} from '@carbon/react/icons';
import { Well as WellIcon } from 'components/icons';
import { type LocationSuggestion, optionId } from './coordinate-utils';
import styles from './coordinate-pins.module.scss';

export interface LocationSuggestionsProps {
  /** The list's id, which the field's `aria-controls` names. */
  id: string;
  suggestions: LocationSuggestion[];
  /** The index the field's arrow keys are on; -1 for none. */
  highlight: number;
  /** Whether the search couldn't run, shown in place of "Searching…". */
  failed: boolean;
  onHighlight: (index: number) => void;
  onPick: (suggestion: LocationSuggestion) => void;
}

/**
 * The Location field's wells, places and survey areas, below it in the card's flow so
 * nothing clips them. Options never take focus: the field keeps it, and its arrows and
 * Enter drive the list, as a combobox's do.
 *
 * @param {LocationSuggestionsProps} props - Component props.
 * @return {JSX.Element} The list.
 */
export function LocationSuggestions({
  id,
  suggestions,
  highlight,
  failed,
  onHighlight,
  onPick,
}: LocationSuggestionsProps): JSX.Element {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label="Wells, places and survey areas"
      className={styles.suggestions}
    >
      {suggestions.length === 0 && (
        <li className={styles.suggestionsStatus}>
          {failed ? "Couldn't search. Try again." : 'Searching…'}
        </li>
      )}
      {suggestions.map((suggestion, index) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- a combobox option never takes focus; the field's arrows and Enter drive it
        <li
          key={suggestion.id}
          id={optionId(id, suggestion)}
          role="option"
          aria-selected={index === highlight}
          className={classNames(styles.suggestion, {
            [styles.suggestionActive]: index === highlight,
          })}
          // Keeps focus in the field, so its blur doesn't close the list first.
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => onHighlight(index)}
          onClick={() => onPick(suggestion)}
        >
          {suggestion.kind === 'well' && (
            <WellIcon hasBackground={false} className={styles.wellIcon} />
          )}
          {suggestion.kind === 'place' && <MapIcon />}
          {suggestion.kind === 'surveyArea' && <SurveyAreaIcon />}
          <span className={styles.suggestionText}>
            <span className={styles.suggestionTitle}>{suggestion.title}</span>
            {suggestion.detail && (
              <span className={styles.suggestionDetail}>
                {suggestion.detail}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
